import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Stack,
  Switch,
  FormControl,
  FormLabel,
  Input,
  Text,
  VStack,
  HStack,
  Tooltip,
  IconButton,
  Divider,
  Badge,
  useColorModeValue,
  Button,
  SimpleGrid,
  Progress,
  Icon,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  InputGroup,
  InputRightElement,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverHeader,
  PopoverCloseButton,
  Portal,
  ButtonGroup,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  SliderMark,
  Flex,
  useToast,
  Grid,
  GridItem,
  Select,
  FormHelperText,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from '@chakra-ui/react';
import { FiHelpCircle, FiClock, FiZap, FiRefreshCw, FiCalendar, FiMessageCircle, FiType, FiSunrise, FiSunset, FiChevronRight, FiChevronLeft, FiLayout } from 'react-icons/fi';
import MessageStyles from './MessageStyles';

const TimerSettings = ({
  active = false,
  start = '',
  end = '',
  messages = {
    default: 'Limited Time Offer! Only {time} left',
    lastDay: 'Hurry! Only {time} remaining',
    lastHour: 'Act Fast! Bundle ends in {time}'
  },
  styles = {},
  onChange
}) => {
  const [previewTime, setPreviewTime] = useState(3600);
  const [isPreviewRunning, setIsPreviewRunning] = useState(false);
  const [progress, setProgress] = useState(100);
  const [selectedTab, setSelectedTab] = useState(0);
  const [duration, setDuration] = useState(24);
  const [datePickerStates, setDatePickerStates] = useState({
    startIsOpen: false,
    endIsOpen: false,
    startSelectedDate: start ? new Date(start) : new Date(),
    endSelectedDate: end ? new Date(end) : new Date(),
    startSelectedTime: {
      hours: start ? new Date(start).getHours() : new Date().getHours(),
      minutes: start ? new Date(start).getMinutes() : new Date().getMinutes()
    },
    endSelectedTime: {
      hours: end ? new Date(end).getHours() : new Date().getHours(),
      minutes: end ? new Date(end).getMinutes() : new Date().getMinutes()
    }
  });
  const [selectedMessageType, setSelectedMessageType] = useState('default');

  const toast = useToast();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const accentColor = useColorModeValue('purple.500', 'purple.300');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const selectedBg = useColorModeValue('purple.100', 'purple.800');
  const selectedColor = useColorModeValue('purple.800', 'purple.100');
  const pickerHoverBg = useColorModeValue('gray.100', 'gray.700');
  const todayBg = useColorModeValue('gray.50', 'gray.600');
  const textColor = useColorModeValue('gray.800', 'white');

  useEffect(() => {
    let interval;
    if (isPreviewRunning && previewTime > 0) {
      interval = setInterval(() => {
        setPreviewTime((prev) => {
          const newTime = prev - 1;
          setProgress((newTime / 3600) * 100);
          return newTime;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isPreviewRunning, previewTime]);

  const formatPreviewTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    }
    return `${minutes}m ${secs}s`;
  };

  const handleChange = (field, value) => {
    onChange({
      active: field === 'active' ? value : active,
      start: field === 'start' ? value : start,
      end: field === 'end' ? value : end,
      messages: field === 'messages' ? value : messages,
      styles: field === 'styles' ? value : styles
    });
  };

  const resetPreview = () => {
    setPreviewTime(3600);
    setProgress(100);
    setIsPreviewRunning(false);
  };

  const commonPresets = [
    { label: '3 Hours', hours: 3, description: 'Flash sale urgency' },
    { label: '12 Hours', hours: 12, description: 'Half-day flash sale' },
    { label: '24 Hours', hours: 24, description: 'Daily deal special' },
    { label: '48 Hours', hours: 48, description: 'Weekend special' },
    { label: '72 Hours', hours: 72, description: 'Extended weekend' },
    { label: '5 Days', hours: 120, description: 'Work week special' },
    { label: '7 Days', hours: 168, description: 'Week-long campaign' },
    { label: '14 Days', hours: 336, description: 'Two week event' }
  ];

  const updateDates = (hours) => {
    const now = new Date();
    const endTime = new Date(now.getTime() + hours * 60 * 60 * 1000);

    const formatDate = (date) => {
      return date.toISOString().slice(0, 16);
    };

    onChange({
      active: true,
      start: formatDate(now),
      end: formatDate(endTime),
      messages,
      styles
    });

    setDuration(hours);
  };

  const validateDates = () => {
    const start = new Date(start);
    const end = new Date(end);

    if (end <= start) {
      toast({
        title: "Invalid date range",
        description: "End date must be after start date",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return false;
    }
    return true;
  };

  const SimpleDateTime = ({ value, onChange, label, icon, colorScheme = "purple" }) => (
    <FormControl>
      <FormLabel>
        <HStack spacing={2}>
          <Icon as={icon} color={`${colorScheme}.500`} />
          <Text>{label}</Text>
        </HStack>
      </FormLabel>
      <Input
        type="datetime-local"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        borderColor={`${colorScheme}.200`}
        _hover={{ borderColor: `${colorScheme}.300` }}
        _focus={{ borderColor: `${colorScheme}.500` }}
      />
    </FormControl>
  );

  const renderScheduleTab = () => (
    <VStack spacing={6}>
      <Box
        p={6}
        bg={useColorModeValue('white', 'gray.800')}
        borderRadius="xl"
        border="1px"
        borderColor={borderColor}
        width="full"
        position="relative"
        overflow="hidden"
        _hover={{ shadow: 'lg' }}
        transition="all 0.2s"
      >
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          h="4px"
          bgGradient="linear(to-r, purple.500, pink.500)"
        />

        <VStack spacing={6}>
          <HStack width="full" justify="space-between">
            <VStack align="start" spacing={1}>
              <Text fontSize="lg" fontWeight="bold">Timer Duration</Text>
              <Text fontSize="sm" color="gray.500">Select how long your offer will last</Text>
            </VStack>
            <Badge
              colorScheme="purple"
              fontSize="sm"
              px={3}
              py={1}
              borderRadius="full"
              bgGradient="linear(to-r, purple.500, pink.500)"
              color="white"
            >
              {duration} hours
            </Badge>
          </HStack>

          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} width="full">
            {commonPresets.map((preset) => (
              <Button
                key={preset.label}
                height="auto"
                p={4}
                variant={duration === preset.hours ? "solid" : "outline"}
                colorScheme="purple"
                onClick={() => updateDates(preset.hours)}
                _hover={{
                  transform: 'translateY(-2px)',
                  shadow: 'md',
                  bgGradient: duration === preset.hours ? 'linear(to-r, purple.500, pink.500)' : 'none'
                }}
                bgGradient={duration === preset.hours ? 'linear(to-r, purple.500, pink.500)' : 'none'}
                transition="all 0.2s"
                position="relative"
                overflow="hidden"
              >
                <VStack spacing={1}>
                  <Text fontWeight="bold">{preset.label}</Text>
                  <Text fontSize="xs" color={duration === preset.hours ? "white" : "gray.500"}>
                    {preset.description}
                  </Text>
                </VStack>
              </Button>
            ))}
          </SimpleGrid>
        </VStack>
      </Box>

      <Box
        p={4}
        bg={hoverBg}
        borderRadius="md"
        width="full"
        border="1px"
        borderColor={borderColor}
        borderStyle="dashed"
      >
        <VStack align="start" spacing={1}>
          <Text fontSize="sm" fontWeight="medium">Quick Tips:</Text>
          <Text fontSize="sm" color="gray.500">• Set shorter durations (24-72 hours) for better urgency</Text>
          <Text fontSize="sm" color="gray.500">• Align timer end with your business hours</Text>
          <Text fontSize="sm" color="gray.500">• Consider timezone differences for global customers</Text>
        </VStack>
      </Box>
    </VStack>
  );

  return (
    <Box bg={bgColor} p={6} borderRadius="xl" boxShadow="sm" border="1px" borderColor={borderColor}>
      <Stack spacing={6}>
        <Tabs colorScheme="purple" index={selectedTab} onChange={setSelectedTab}>
          <SimpleGrid columns={2} spacing={4} mb={6}>
            <Box
              as={Tab}
              p={4}
              bg={useColorModeValue('white', 'gray.800')}
              borderRadius="xl"
              border="1px"
              borderColor={selectedTab === 0 ? 'purple.500' : borderColor}
              boxShadow={selectedTab === 0 ? 'lg' : 'sm'}
              _hover={{
                borderColor: 'purple.500',
                transform: 'translateY(-2px)',
                boxShadow: 'lg'
              }}
              transition="all 0.2s"
              cursor="pointer"
              position="relative"
              overflow="hidden"
            >
              {selectedTab === 0 && (
                <Box
                  position="absolute"
                  top={0}
                  left={0}
                  right={0}
                  h="3px"
                  bgGradient="linear(to-r, purple.500, pink.500)"
                />
              )}
              <VStack spacing={3} align="center">
                <Badge
                  colorScheme="purple"
                  variant="solid"
                  borderRadius="full"
                  px={3}
                  py={1}
                  fontSize="xs"
                >
                  Step 1
                </Badge>
                <Icon
                  as={FiCalendar}
                  boxSize={6}
                  color={selectedTab === 0 ? 'purple.500' : 'gray.400'}
                />
                <VStack spacing={0}>
                  <Text
                    fontSize="lg"
                    fontWeight="bold"
                    color={selectedTab === 0 ? 'purple.500' : 'inherit'}
                  >
                    Schedule
                  </Text>
                  <Text
                    fontSize="xs"
                    color={useColorModeValue('gray.600', 'gray.400')}
                    textAlign="center"
                  >
                    Set duration & dates
                  </Text>
                </VStack>
              </VStack>
            </Box>

            <Box
              as={Tab}
              p={4}
              bg={useColorModeValue('white', 'gray.800')}
              borderRadius="xl"
              border="1px"
              borderColor={selectedTab === 1 ? 'purple.500' : borderColor}
              boxShadow={selectedTab === 1 ? 'lg' : 'sm'}
              _hover={{
                borderColor: 'purple.500',
                transform: 'translateY(-2px)',
                boxShadow: 'lg'
              }}
              transition="all 0.2s"
              cursor="pointer"
              position="relative"
              overflow="hidden"
            >
              {selectedTab === 1 && (
                <Box
                  position="absolute"
                  top={0}
                  left={0}
                  right={0}
                  h="3px"
                  bgGradient="linear(to-r, purple.500, pink.500)"
                />
              )}
              <VStack spacing={3} align="center">
                <Badge
                  colorScheme="purple"
                  variant="solid"
                  borderRadius="full"
                  px={3}
                  py={1}
                  fontSize="xs"
                >
                  Step 2
                </Badge>
                <Icon
                  as={FiType}
                  boxSize={6}
                  color={selectedTab === 1 ? 'purple.500' : 'gray.400'}
                />
                <VStack spacing={0}>
                  <Text
                    fontSize="lg"
                    fontWeight="bold"
                    color={selectedTab === 1 ? 'purple.500' : 'inherit'}
                  >
                    Design & Content
                  </Text>
                  <Text
                    fontSize="xs"
                    color={useColorModeValue('gray.600', 'gray.400')}
                    textAlign="center"
                  >
                    Customize appearance
                  </Text>
                </VStack>
              </VStack>
            </Box>
          </SimpleGrid>

          <TabPanels>
            <TabPanel>
              {renderScheduleTab()}
            </TabPanel>

            {/* Design & Content Tab */}
            <TabPanel>
              <Grid templateColumns="minmax(300px, 400px) 1fr" gap={8}>
                {/* Left Panel - Message Selection and Styling */}
                <Box>
                  <Accordion allowToggle>
                    {/* Message Type Selection */}
                    <AccordionItem>
                      {({ isExpanded }) => (
                        <>
                          <h2>
                            <AccordionButton
                              py={2}
                              px={3}
                              bg={isExpanded ? 'purple.50' : 'transparent'}
                              _hover={{ bg: isExpanded ? 'purple.50' : 'gray.50' }}
                            >
                              <HStack flex="1" spacing={2}>
                                <Icon as={FiType} color="purple.500" boxSize="18px" />
                                <Text fontWeight="medium">Message Type</Text>
                              </HStack>
                              <AccordionIcon />
                            </AccordionButton>
                          </h2>
                          <AccordionPanel pb={3} px={3}>
                            <SimpleGrid columns={1} spacing={3}>
                              {Object.entries({
                                default: { label: 'Default Message', icon: FiClock },
                                lastDay: { label: 'Last Day', icon: FiSunset },
                                lastHour: { label: 'Last Hour', icon: FiZap }
                              }).map(([value, { label, icon }]) => (
                                <Button
                                  key={value}
                                  onClick={() => setSelectedMessageType(value)}
                                  variant={selectedMessageType === value ? 'solid' : 'outline'}
                                  colorScheme="purple"
                                  size="lg"
                                  leftIcon={<Icon as={icon} />}
                                  justifyContent="flex-start"
                                  h="auto"
                                  py={3}
                                  px={4}
                                  _hover={{
                                    transform: 'translateY(-1px)',
                                    shadow: 'md'
                                  }}
                                  transition="all 0.2s"
                                >
                                  {label}
                                </Button>
                              ))}
                            </SimpleGrid>
                          </AccordionPanel>
                        </>
                      )}
                    </AccordionItem>

                    {/* Message Text */}
                    <AccordionItem>
                      {({ isExpanded }) => (
                        <>
                          <h2>
                            <AccordionButton
                              py={2}
                              px={3}
                              bg={isExpanded ? 'purple.50' : 'transparent'}
                              _hover={{ bg: isExpanded ? 'purple.50' : 'gray.50' }}
                            >
                              <HStack flex="1" spacing={2}>
                                <Icon as={FiMessageCircle} color="purple.500" boxSize="18px" />
                                <Text fontWeight="medium">Message Text</Text>
                              </HStack>
                              <AccordionIcon />
                            </AccordionButton>
                          </h2>
                          <AccordionPanel pb={3} px={3}>
                            <VStack spacing={4} align="stretch">
                              <Input
                                value={messages[selectedMessageType]}
                                onChange={(e) => handleChange('messages', {
                                  ...messages,
                                  [selectedMessageType]: e.target.value
                                })}
                                placeholder="Enter your message here..."
                                size="lg"
                                variant="filled"
                                _focus={{
                                  borderColor: 'purple.500',
                                  boxShadow: '0 0 0 1px var(--chakra-colors-purple-500)'
                                }}
                              />
                              <HStack spacing={2} color="gray.500">
                                <Icon as={FiHelpCircle} />
                                <Text fontSize="sm">Use {'{time}'} to show the countdown</Text>
                              </HStack>
                            </VStack>
                          </AccordionPanel>
                        </>
                      )}
                    </AccordionItem>

                    {/* Style Options */}
                    <AccordionItem>
                      {({ isExpanded }) => (
                        <>
                          <h2>
                            <AccordionButton
                              py={2}
                              px={3}
                              bg={isExpanded ? 'purple.50' : 'transparent'}
                              _hover={{ bg: isExpanded ? 'purple.50' : 'gray.50' }}
                            >
                              <HStack flex="1" spacing={2}>
                                <Icon as={FiLayout} color="purple.500" boxSize="18px" />
                                <Text fontWeight="medium">Style Options</Text>
                              </HStack>
                              <AccordionIcon />
                            </AccordionButton>
                          </h2>
                          <AccordionPanel pb={3} px={3}>
                            <MessageStyles
                              styles={styles}
                              onChange={(newStyles) => handleChange('styles', newStyles)}
                            />
                          </AccordionPanel>
                        </>
                      )}
                    </AccordionItem>
                  </Accordion>
                </Box>

                {/* Right Panel - Live Preview */}
                <Box>
                  <Box
                    position="sticky"
                    top="24px"
                    p={8}
                    bg={useColorModeValue('gray.50', 'gray.700')}
                    borderRadius="xl"
                    border="2px"
                    borderStyle="dashed"
                    borderColor={borderColor}
                    minH="600px"
                    maxH="calc(100vh - 48px)"
                    overflowY="auto"
                    css={{
                      '&::-webkit-scrollbar': {
                        width: '4px',
                      },
                      '&::-webkit-scrollbar-track': {
                        width: '6px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: 'var(--chakra-colors-gray-300)',
                        borderRadius: '24px',
                      },
                    }}
                    _before={{
                      content: '""',
                      position: 'absolute',
                      top: '-2px',
                      left: '-2px',
                      right: '-2px',
                      height: '4px',
                      bgGradient: 'linear(to-r, purple.500, pink.500)',
                      borderTopRadius: 'xl'
                    }}
                  >
                    <VStack spacing={8}>
                      <HStack
                        bg={useColorModeValue('white', 'gray.800')}
                        p={3}
                        px={5}
                        borderRadius="full"
                        shadow="sm"
                      >
                        <Icon as={FiClock} color="purple.500" />
                        <Text fontWeight="medium">Live Preview</Text>
                      </HStack>

                      <Box
                        p={8}
                        bg={bgColor}
                        borderRadius="xl"
                        width="full"
                        maxW="800px"
                        mx="auto"
                        boxShadow="lg"
                        border="1px"
                        borderColor={borderColor}
                        position="relative"
                        overflow="hidden"
                      >
                        <VStack spacing={8}>
                          {/* Timer Preview */}
                          <Box
                            p={styles?.container?.padding || '16px'}
                            bg={styles?.container?.backgroundColor || 'white'}
                            borderRadius={styles?.container?.borderRadius ? `${styles.container.borderRadius}px` : 'md'}
                            borderWidth={styles?.container?.borderWidth || '1px'}
                            borderStyle={styles?.container?.borderStyle || 'solid'}
                            borderColor={styles?.container?.borderColor || borderColor}
                            width="full"
                            textAlign="center"
                            transition="all 0.3s"
                            _hover={{ transform: 'scale(1.01)', shadow: 'md' }}
                          >
                            <Text
                              fontSize={styles?.message?.fontSize || 'md'}
                              fontWeight={styles?.message?.fontWeight || 'normal'}
                              color={styles?.message?.color || 'inherit'}
                              textAlign={styles?.message?.textAlign || 'center'}
                            >
                              {messages[selectedMessageType]?.replace('{time}', formatPreviewTime(previewTime))}
                            </Text>
                          </Box>

                          {/* Preview Controls */}
                          <VStack spacing={4} width="full">
                            <HStack spacing={4} justify="center">
                              <Button
                                size="md"
                                colorScheme="purple"
                                onClick={() => setIsPreviewRunning(!isPreviewRunning)}
                                leftIcon={isPreviewRunning ? <FiRefreshCw /> : <FiClock />}
                                px={6}
                                _hover={{
                                  transform: 'translateY(-1px)',
                                  shadow: 'md'
                                }}
                                transition="all 0.2s"
                              >
                                {isPreviewRunning ? 'Pause' : 'Start'} Preview
                              </Button>
                              <Button
                                size="md"
                                variant="outline"
                                onClick={resetPreview}
                                leftIcon={<FiRefreshCw />}
                                px={6}
                                _hover={{
                                  transform: 'translateY(-1px)',
                                  shadow: 'sm'
                                }}
                                transition="all 0.2s"
                              >
                                Reset
                              </Button>
                            </HStack>

                            <Box width="full" maxW="md" mx="auto">
                              <Progress
                                value={progress}
                                size="sm"
                                colorScheme="purple"
                                borderRadius="full"
                                hasStripe
                                isAnimated={isPreviewRunning}
                              />
                            </Box>
                          </VStack>
                        </VStack>
                      </Box>
                    </VStack>
                  </Box>
                </Box>
              </Grid>
            </TabPanel>
          </TabPanels>
        </Tabs>

        <Box pt={4}>
          <Text fontSize="sm" color="gray.500">
            The timer will automatically adjust messages based on remaining time.
            Preview your messages above to ensure they look perfect.
          </Text>
        </Box>
      </Stack>
    </Box>
  );
};

export default TimerSettings;